import { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Button, Field, Input } from '@mgdlead/ui';

import { AuthLayout } from '../../components/AuthLayout';
import { FormAlert } from '@mgdlead/ui';
import { readNextPath } from '../../auth/guards';
import { signUpWithPassword } from '../../lib/auth';
import { signupSchema } from '../../lib/schemas';
import type { SignupValues } from '../../lib/schemas';
import { useDocumentTitle } from '../../lib/useDocumentTitle';
import { useZodForm } from '../../lib/useZodForm';

const INITIAL: SignupValues = { name: '', email: '', password: '', passwordConfirmation: '' };

export function SignupPage() {
  useDocumentTitle('Criar conta');
  const location = useLocation();
  const navigate = useNavigate();
  const next = readNextPath(location.search);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const onSubmit = useCallback(
    async (values: {
      name: string;
      email: string;
      password: string;
      passwordConfirmation: string;
    }) => {
      await signUpWithPassword(values);
      // não mandamos direto para o painel: o e-mail ainda precisa ser confirmado
      setSentTo(values.email);
    },
    [],
  );

  const form = useZodForm({ schema: signupSchema, initialValues: INITIAL, onSubmit });

  if (sentTo !== null) {
    return (
      <AuthLayout
        title="Confirme seu e-mail"
        description={`Enviamos um link de confirmação para ${sentTo}. Abra o link para ativar a conta.`}
        footer={
          <Link
            to="/login"
            className="font-medium text-primary-text underline-offset-4 hover:underline"
          >
            Voltar para entrar
          </Link>
        }
      >
        <FormAlert tone="info">
          Não chegou em até 2 minutos? Confira a caixa de spam e depois peça um novo link na tela de
          verificação.
        </FormAlert>
        {/* botão dentro de <a> é HTML inválido: quem navega é o router */}
        <Button
          block
          onClick={() => void navigate(`/verificar-email?email=${encodeURIComponent(sentTo)}`)}
        >
          Ir para a verificação
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Criar conta"
      description="Leva menos de um minuto. Depois você cria o primeiro workspace."
      footer={
        <>
          Já tem conta?{' '}
          <Link
            to={next ? `/login?next=${encodeURIComponent(next)}` : '/login'}
            className="font-medium text-primary-text underline-offset-4 hover:underline"
          >
            Entrar
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-4">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field label="Nome" error={form.errors.name} required>
          {(control) => (
            <Input
              {...control}
              name="name"
              autoComplete="name"
              placeholder="Como devemos te chamar"
              value={form.values.name}
              invalid={form.errors.name !== undefined}
              onChange={(event) => form.setField('name', event.target.value)}
            />
          )}
        </Field>

        <Field label="E-mail" error={form.errors.email} required>
          {(control) => (
            <Input
              {...control}
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="voce@empresa.com"
              value={form.values.email}
              invalid={form.errors.email !== undefined}
              onChange={(event) => form.setField('email', event.target.value)}
            />
          )}
        </Field>

        <Field label="Senha" hint="Mínimo de 8 caracteres." error={form.errors.password} required>
          {(control) => (
            <Input
              {...control}
              type="password"
              name="password"
              autoComplete="new-password"
              value={form.values.password}
              invalid={form.errors.password !== undefined}
              onChange={(event) => form.setField('password', event.target.value)}
            />
          )}
        </Field>

        <Field label="Repita a senha" error={form.errors.passwordConfirmation} required>
          {(control) => (
            <Input
              {...control}
              type="password"
              name="passwordConfirmation"
              autoComplete="new-password"
              value={form.values.passwordConfirmation}
              invalid={form.errors.passwordConfirmation !== undefined}
              onChange={(event) => form.setField('passwordConfirmation', event.target.value)}
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="dark" size="lg"
          block
          loading={form.pending}
          loadingLabel="Criando conta"
        >
          Criar conta
        </Button>
      </form>
    </AuthLayout>
  );
}
